import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppComponent } from '../app.component';
import { ApiService } from '../services/api.service';

@Component({
  selector: 'app-add-media',
  templateUrl: './add-media.component.html',
  styleUrls: ['./add-media.component.scss']
})
export class AddMediaComponent implements OnInit {

  public form: FormGroup;
  public selectedFiles: File[] = [];
  public isEditMode = false;
  public mediaId: any;

  public categories = [
     'Bhasma Aarti',
                'Dadyodak Aarti',
                'Bhog Aarti',
                'Sandhya Aarti',
                'Shayan Aarti',
                'General Darshan'
  ];
  public mediaTypes = [
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' }
  ];

  public uploadedMedia: any[] = [];
  public isUploading = false;
  public uploadProgress = 0;

  constructor(
    private apiService: ApiService,
    private appComponent: AppComponent,
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    // Initialize form with validators
    this.form = this.fb.group({
      category: ['', Validators.required],
      date: [new Date().toISOString().split('T')[0], Validators.required],
      media_type: ['image', Validators.required]
    });

    // Check if we're in edit mode
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.isEditMode = true;
        this.mediaId = id;
        this.loadMediaData(id);
      }
    });
  }

  loadMediaData(id: string) {
    this.appComponent.showLoader();
    this.apiService.getDataFromServer(`/media/${id}`).subscribe(
      (res: any) => {
        this.appComponent.hideLoader();
        if (res && res.data) {
          // Format date for input field (YYYY-MM-DD)
          let formattedDate = res.data.date;
          if (res.data.date) {
            // Handle different date formats and convert to YYYY-MM-DD
            const dateObj = new Date(res.data.date);
            if (!isNaN(dateObj.getTime())) {
              formattedDate = dateObj.toISOString().split('T')[0];
            } else {
              // Try to handle DD-MM-YYYY format
              const dateParts = res.data.date.split('-');
              if (dateParts.length === 3) {
                formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
              }
            }
          }
          
          // Populate form with existing data
          this.form.patchValue({
            category: res.data.category,
            date: formattedDate,
            media_type: res.data.media_type
          });
          
          // Load existing media
          if (res.data.media && Array.isArray(res.data.media)) {
            this.uploadedMedia = res.data.media;
          } else if (res.data.media_url) {
            // Handle single media item
            this.uploadedMedia = [{
              media_type: res.data.media_type,
              media_url: res.data.media_url,
              thumbnail_url: res.data.thumbnail_url
            }];
          }
        }
      },
      err => {
        this.appComponent.hideLoader();
        console.log(err);
        this.appComponent.showMessageAlert('Error loading media data', 'error');
        this.router.navigate(['/media-list']);
      }
    );
  }

  async onFileSelect(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Validate form fields first
    if (!this.form.valid) {
      this.appComponent.showMessageAlert('Please fill Category, Date, and Media Type before selecting files', 'error');
      event.target.value = '';
      return;
    }

    const selectedFiles: File[] = Array.from(files);
    const category = this.form.get('category').value;
    const date = this.form.get('date').value;
    const mediaType = this.form.get('media_type').value;
    
    // Validate each file
    for (let file of selectedFiles) {
      // Validate file type
      const fileType = file.type.split('/')[0];
      
      if (mediaType === 'image' && fileType !== 'image') {
        this.appComponent.showMessageAlert('Please select only image files', 'error');
        event.target.value = '';
        return;
      }
      if (mediaType === 'video' && fileType !== 'video') {
        this.appComponent.showMessageAlert('Please select only video files', 'error');
        event.target.value = '';
        return;
      }

      // Validate file size (max 500MB)
      if (file.size > 500 * 1024 * 1024) {
        this.appComponent.showMessageAlert(`File ${file.name} size should be less than 500MB`, 'error');
        event.target.value = '';
        return;
      }
    }

    // Upload files immediately
    this.isUploading = true;
    this.uploadProgress = 0;

    try {
      const totalFiles = selectedFiles.length;
      let uploadedCount = 0;

      // Upload each file
      for (let file of selectedFiles) {
        const formData = new FormData();
        formData.append('category', category);
        formData.append('date', date);
        formData.append('media_type', mediaType);
        formData.append('media', file);

        // Upload media file to /api/v1/media/upload
        const uploadResponse: any = await this.apiService.postDataToServer('/media/upload', formData).toPromise();
        
        if (uploadResponse && uploadResponse.data) {
          // Add uploaded media to the list with URLs from response
          this.uploadedMedia.push({
            media_type: mediaType,
            media_url: uploadResponse.data.media_url,
            thumbnail_url: uploadResponse.data.thumbnail_url
          });
        }

        uploadedCount++;
        this.uploadProgress = Math.round((uploadedCount / totalFiles) * 100);
      }

      // Clear file input
      event.target.value = '';

      this.appComponent.showMessageAlert(`${uploadedCount} file(s) uploaded successfully. You can add more or submit.`, 'success');
    } catch (error) {
      console.error('Upload error:', error);
      this.appComponent.showMessageAlert('Error uploading media files', 'error');
      event.target.value = '';
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
    }
  }

  removeUploadedMedia(index: number) {
    this.uploadedMedia.splice(index, 1);
  }

  async submitMedia() {
    if (this.uploadedMedia.length === 0) {
      this.appComponent.showMessageAlert('Please upload at least one media file', 'error');
      return;
    }

    if (!this.form.valid) {
      this.appComponent.showMessageAlert('Please fill all required fields', 'error');
      return;
    }

    this.appComponent.showLoader();

    try {
      const payload = {
        category: this.form.get('category').value,
        date: this.form.get('date').value,
        media: this.uploadedMedia
      };

      let response: any;
      if (this.isEditMode) {
        // Update existing media
        response = await this.apiService.putDataToServer(`/media/${this.mediaId}`, payload).toPromise();
        this.appComponent.hideLoader();
        this.appComponent.showMessageAlert('Media updated successfully', 'success');
      } else {
        // Create new media
        response = await this.apiService.postDataToServer('/media', payload).toPromise();
        this.appComponent.hideLoader();
        this.appComponent.showMessageAlert('Media submitted successfully', 'success');
      }
      
      // Navigate back to media list
      this.router.navigate(['/media-list']);
    } catch (error) {
      this.appComponent.hideLoader();
      console.error('Submit error:', error);
      this.appComponent.showMessageAlert('Error ' + (this.isEditMode ? 'updating' : 'submitting') + ' media', 'error');
    }
  }

  cancel() {
    this.router.navigate(['/media-list']);
  }

  resetForm() {
    this.form.reset({
      category: '',
      date: new Date().toISOString().split('T')[0],
      media_type: 'image'
    });
    this.selectedFiles = [];
    this.uploadedMedia = [];
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  getMediaTypeIcon(mediaType: string) {
    return mediaType === 'video' ? 'fa-video-camera' : 'fa-picture-o';
  }
}
